import { useFrame } from "@react-three/fiber"
import { useMemo, useRef } from "react"
import { BufferGeometry, Float32BufferAttribute, Points } from "three"

interface ParticlesProps {
  count?: number
  radius?: number
}

export function Particles({ count = 720, radius = 14 }: ParticlesProps) {
  const ref = useRef<Points>(null)
  const pointer = useRef({ x: 0, y: 0 })

  const geometry = useMemo(() => {
    const positions = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      const u = Math.random()
      const v = Math.random()
      const theta = u * Math.PI * 2
      const phi = Math.acos(2 * v - 1)
      const r = radius * (0.42 + Math.random() * 0.58)
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta)
      positions[i * 3 + 2] = r * Math.cos(phi)
    }
    const geo = new BufferGeometry()
    geo.setAttribute("position", new Float32BufferAttribute(positions, 3))
    return geo
  }, [count, radius])

  useFrame((state, delta) => {
    if (!ref.current) return
    pointer.current.x = state.pointer.x
    pointer.current.y = state.pointer.y
    ref.current.rotation.y += delta * 0.018
    ref.current.rotation.x += delta * 0.006
    ref.current.rotation.z = pointer.current.x * 0.04
    ref.current.position.x = pointer.current.x * 0.18
    ref.current.position.y = -pointer.current.y * 0.12
    const mat = ref.current.material as { opacity?: number } | undefined
    if (mat && typeof mat.opacity === "number") {
      mat.opacity = 0.55 + Math.sin(state.clock.elapsedTime * 0.6) * 0.08
    }
  })

  return (
    <points ref={ref} geometry={geometry} frustumCulled={false}>
      <pointsMaterial
        color="#7af0d8"
        size={0.045}
        sizeAttenuation
        transparent
        opacity={0.6}
        depthWrite={false}
      />
    </points>
  )
}
